import { useState, useEffect, type SetStateAction, type Dispatch } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '../../store';
import type { QueryDefinition } from '@reduxjs/toolkit/dist/query';
import type { UseQuery, UseQueryHookResult } from '@reduxjs/toolkit/dist/query/react/buildHooks';

/**
 * Utility wrappers for `useQuery` hooks.
 */
export namespace Query {
  /**
   * Retrieve the result type from a `QueryDefinition`.
   */
  type DefResult<Def extends QueryDefinition<any, any, any, any>> = Def extends QueryDefinition<
      any,
      any,
      any,
      infer Result
  >
      ? Result
      : never;

  export const withDraft = <Def extends QueryDefinition<any, any, any, any>>(
      hook: UseQuery<Def>,
      initialState?: DefResult<Def>,
      merge?: (fetched: DefResult<Def>, init: DefResult<Def> | undefined) => DefResult<Def>
  ) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return ((...args: Parameters<typeof hook>) => {
          const { data, ...rest } = hook(...args);
          const [draft, setDraft] = useState(initialState ?? data);

          useEffect(() => {
              // If the data is truthy, sync the draft with it.
              // Otherwise, reset back to the initial state (which can
              // be undefined, just like "data")
              setDraft(data ? (typeof merge === 'function' ? merge(data, initialState) : data) : initialState);
          }, [data]);

          return {
              draft: [draft, setDraft] as const,
              data,
              ...rest,
          };
          // Exactly the same function as what was inputted, except returning a draft state
          // tuple along with the rest of the return type.
      }) as (...params: Parameters<UseQuery<Def>>) => UseQueryHookResult<Def> & {
          draft: [DefResult<Def>, Dispatch<SetStateAction<DefResult<Def>>>];
      };
  };

  type UseQueryMinusToken<Def extends QueryDefinition<any, any, any, any>> = Def extends QueryDefinition<
      infer Params,
      infer Second,
      infer Third,
      infer Fourth,
      infer Fifth
  >
      ? UseQuery<
            // The definition is exactly the same. However, if the input's params type accepts
            // "token", that property should not be required.
            QueryDefinition<
                // ! Things break if this conditional is switched to just "Omit<Params, 'token'>"
                Params extends { token: string } ? Omit<Params, 'token'> : Params,
                Second,
                Third,
                Fourth,
                Fifth
            >
        >
      : never;

  export const withToken = <Def extends QueryDefinition<any, any, any, any>>(hook: UseQuery<Def>) =>
      ((...[first, ...rest]) => {
          if (typeof first !== 'object') {
              return hook(first, ...rest);
          }

          const IdToken = useSelector((state: RootState) => state.auth.IdToken);

          return hook({ ...first, token: IdToken }, ...rest);
      }) as UseQueryMinusToken<Def>;
}

